import { StaticMapPin } from './StaticMap.types'

const TILE_SIZE_PX = 256
const MAX_ZOOM = 16
const MIN_ZOOM = 10
/** Keeps the pins off the edges of the image, where a marker would be clipped. */
const PADDING = 1.6

/** Mercator's y, as a fraction of the world, for a latitude. */
const mercatorY = (latitude: number): number => {
  const sin = Math.sin((latitude * Math.PI) / 180)
  return Math.log((1 + sin) / (1 - sin)) / 2
}

const zoomForSpan = (availablePx: number, worldFraction: number): number => {
  if (worldFraction <= 0) {
    return MAX_ZOOM
  }
  return Math.log2(availablePx / (TILE_SIZE_PX * worldFraction * PADDING))
}

export const serialisePins = (pins: StaticMapPin[]): string =>
  pins
    .map(({ latitude, longitude, label }) =>
      [latitude, longitude, label].filter((part) => part != null && part !== '').join(',')
    )
    .join('|')

/**
 * The closest zoom that still shows every pin.
 *
 * Google frames markers itself when no zoom is given, which for two desks a few metres apart means a
 * featureless close-up of one roof. Fitting the pins ourselves and clamping keeps the airport around
 * them, so the map answers "where is this" as well as "which desk".
 */
export const getFittingZoom = (
  pins: StaticMapPin[],
  widthPx: number,
  heightPx: number
): number | undefined => {
  if (pins.length < 2 || widthPx <= 0 || heightPx <= 0) {
    return undefined
  }

  const latitudes = pins.map((pin) => pin.latitude)
  const longitudes = pins.map((pin) => pin.longitude)
  const latitudeFraction =
    (mercatorY(Math.max(...latitudes)) - mercatorY(Math.min(...latitudes))) / Math.PI
  const longitudeFraction = (Math.max(...longitudes) - Math.min(...longitudes)) / 360

  const zoom = Math.min(
    zoomForSpan(heightPx, latitudeFraction),
    zoomForSpan(widthPx, longitudeFraction)
  )
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.floor(zoom)))
}
